import { Box, Stack, Theme } from '@mui/material';
import { ClassAttributes, CSSProperties, FC, HTMLAttributes, LegacyRef } from 'react';
import { ExtraProps } from 'react-markdown';
import DoneIcon from '../icon/done-icon';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { darcula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { createUseStyles } from 'react-jss';
import copy from 'copy-to-clipboard';
import Text from '../text';

const useStyles = createUseStyles((theme: Theme) => ({
  codeBlockHeader: {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.background.sidebar : theme.palette.grey[400],
    padding: '4px 12px',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  codeBlock: {
    marginBottom: '0px !important',
    marginTop: '0px !important',
    '& code': {
      background: 'transparent !important',
    },
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
}));

type Props = ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement> & ExtraProps;

const CodeBlock: FC<Props> = (props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, className, ref, node, ...rest } = props;
  const [, language] = String(className).match(/language-(\w+)/) || [];
  const styles = useStyles();
  const isCodeBlock = String(children).split('\n').length > 1;

  if (!language && !isCodeBlock) {
    return (
      <code {...rest} className={className}>
        {children}
      </code>
    );
  }

  const codeContent = String(children).replace(/\n$/, '');

  const handleCopyCode = () => {
    copy(codeContent);
  };

  return (
    <Box maxWidth="100%" minWidth={0}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={styles.codeBlockHeader}
      >
        <Text size="small">{String(language || 'TEXT').toLowerCase()}</Text>
        <DoneIcon size="small" name="copy" onClick={handleCopyCode} />
      </Stack>
      <SyntaxHighlighter
        {...rest}
        ref={ref as LegacyRef<SyntaxHighlighter> | undefined}
        PreTag="div"
        children={codeContent}
        language={language}
        style={darcula as Record<string, CSSProperties>}
        className={styles.codeBlock}
        customStyle={{
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'auto',
        }}
      />
    </Box>
  );
};

export default CodeBlock;
